import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Section, usePoll, RangePicker, defaultRange, rangeParams, ExportButton, Pager, fmtTime, TONE } from "@/components/monitor/shared";

const SEV = { critical: ["Kritik", TONE.bad], warning: ["Uyarı", TONE.warn] };

export function IncidentRows({ items, compact }) {
  if (!items?.length) return <div className="text-sm text-slate-400 py-3">Kayıt yok</div>;
  return (
    <div className="divide-y divide-slate-100">
      {items.map((i) => (
        <div key={i.id} data-testid={`incident-${i.id}`} className="py-2.5 flex flex-col sm:flex-row sm:items-start gap-2 justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className={`text-[10px] ${SEV[i.severity]?.[1]}`}>{SEV[i.severity]?.[0]}</Badge>
              <span className="text-sm font-semibold text-slate-900">{i.type_label || i.component}</span>
              {!i.ended_at && <Badge variant="outline" className={`text-[10px] ${TONE.bad}`}>Devam ediyor</Badge>}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{i.component}{i.value != null && ` · değer ${i.value}`}{i.peak_value != null && i.peak_value !== i.value && ` (tepe ${i.peak_value})`}{i.threshold != null && ` · sınır ${i.threshold}`}</div>
            {!compact && i.detail && <div className="text-xs text-slate-600 mt-0.5">{i.detail}</div>}
            {!compact && <div className="text-[11px] text-slate-400 mt-0.5 italic">{i.probable_cause}{!i.cause_verified && " — doğrulanmamış"}</div>}
          </div>
          <div className="text-[11px] text-slate-500 shrink-0 sm:text-right">
            <div>Başlangıç: {fmtTime(i.started_at)}</div>
            <div>Bitiş: {i.ended_at ? fmtTime(i.ended_at) : "—"}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ErrorRows({ items, compact }) {
  if (!items?.length) return <div className="text-sm text-slate-400 py-3">Kayıtlı hata yok</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs">{["Zaman", "İstek", "Durum", "Süre", ...(compact ? [] : ["Hata"])].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
        <tbody>
          {items.map((e) => (
            <tr key={e.id} data-testid={`error-${e.id}`} className="border-t border-slate-100 align-top">
              <td className="px-3 py-2 whitespace-nowrap text-xs">{fmtTime(e.ts)}</td>
              <td className="px-3 py-2 font-mono text-xs break-all">{e.method} {e.path}</td>
              <td className="px-3 py-2"><Badge variant="outline" className={`text-[10px] ${TONE.bad}`}>{e.status}</Badge></td>
              <td className="px-3 py-2 text-xs whitespace-nowrap">{e.duration_ms} ms</td>
              {!compact && <td className="px-3 py-2 text-xs text-slate-600"><b>{e.error_type}</b>{e.detail && <div className="break-all">{e.detail}</div>}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const sel = "h-8 rounded-lg border border-slate-200 px-2 text-xs bg-white";

export default function ErrorsTab() {
  const [range, setRange] = useState(defaultRange(24 * 7));
  const [ip, setIp] = useState(1), [ep, setEp] = useState(1);
  const [type, setType] = useState(""), [status, setStatus] = useState("");
  const { data: labels } = usePoll("/admin/monitor/settings", 0);
  const inc = usePoll("/admin/monitor/incidents", 30000, { ...rangeParams(range), page: ip, size: 15, ...(type && { type }), ...(status && { status }) }).data;
  const err = usePoll("/admin/monitor/errors", 30000, { ...rangeParams(range), page: ep, size: 15 }).data;
  const withLabel = (items) => items?.map((i) => ({ ...i, type_label: labels?.labels?.[i.type] }));
  return (
    <div className="space-y-5" data-testid="monitor-errors">
      <RangePicker range={range} setRange={(r) => { setRange(r); setIp(1); setEp(1); }} testId="err-range" />
      <Section title="Alarm ve uyarı olayları" testId="incidents-section" right={<div className="flex flex-wrap gap-2">
        <select data-testid="incident-type-filter" className={sel} value={type} onChange={(e) => { setType(e.target.value); setIp(1); }}>
          <option value="">Tüm türler</option>{Object.entries(labels?.labels || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select data-testid="incident-status-filter" className={sel} value={status} onChange={(e) => { setStatus(e.target.value); setIp(1); }}>
          <option value="">Tümü</option><option value="open">Devam eden</option><option value="closed">Biten</option>
        </select>
        <ExportButton kind="incidents" range={range} />
      </div>}>
        {!inc ? "Yükleniyor..." : <><IncidentRows items={withLabel(inc.items)} /><Pager page={ip} size={15} total={inc.total} setPage={setIp} testId="incidents-pager" /></>}
      </Section>
      <Section title="Sistem hataları (5xx ve beklenmeyen hatalar)" testId="errors-section" right={<ExportButton kind="errors" range={range} />}>
        {!err ? "Yükleniyor..." : <><ErrorRows items={err.items} /><Pager page={ep} size={15} total={err.total} setPage={setEp} testId="errors-pager" /></>}
      </Section>
      <p className="text-[11px] text-slate-400">Hata ayrıntılarındaki anahtar, parola ve bağlantı bilgileri kaydedilmeden önce maskelenir. Nedenler aksi belirtilmedikçe tahminidir.</p>
    </div>
  );
}
